import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  detectPublicViewport,
  getGridPlacement,
  gridStyle,
  resolveBackgroundStyle,
  resolveComponentBackground,
  resolveComponentPadding,
  resolveInheritedFontStyle,
  resolveFontFamilyChoice,
  resolveMenuTabTypographyStyle,
  resolveSiteFontStyle,
  resolveButtonStyle,
  resolveDropdownStyle,
  resolveBadgeStyle,
  resolveShapeRadius,
  scrollToAnchor,
  scrollToPageTop,
  sortComponentsByGrid,
  componentStackStyle,
  isComponentHidden,
  isSectionHidden,
  getMenuTabs,
  splitMenuTabsByBarAlign,
  normalizeExternalUrl,
  resolveColorFillCss,
  GRID_COLUMNS,
  GRID_ROW_HEIGHT,
  getComponentLayout,
} from '../utils/defaults';
import {
  resolveSectionMinGridRows,
  resolveSectionFixedHeightPx,
  resolveSectionTotalGridRows,
} from '../utils/sectionGrid';
import { resolvePublicPath } from '../utils/commerce';
import { useCommerceSectionExpandRegistry } from '../utils/commerceSectionExpand';
import {
  isJardinsHeroViewportFill,
  resolveJardinsFluidScale,
  useJardinsLayoutWidth,
} from '../utils/jardinsFluid';
import { resolveSectionBackgroundOverlayStyle } from '../utils/sectionOverlay';
import { resolveColorFillStyle, resolveTextFillStyle, resolveIconStrokeColor } from '../utils/colorFillUtils';
import SiteFontStyles from './SiteFontStyles';
import Icon from '../../../components/AppIcon';
import { normalizeIconName } from '../utils/iconCatalog';
import HubupQrView from './HubupQrView';
import HubupNotificationsView from './HubupNotificationsView';
import ExternalSiteModal from './ExternalSiteModal';
import ProductCardView from './ProductCardView';
import {
  CardFeatureView,
  CardIllustrationView,
  CardSplitView,
} from './CardComponentViews';
import ProductPurchaseModal from './ProductPurchaseModal';
import ClickCollectPublicView from './ClickCollectPublicView';
import ReservationPublicView from './ReservationPublicView';
import RestaurantExempleView from './restaurant-exemple/RestaurantExempleView';
import RestaurantSectionBlock from './restaurant-exemple/RestaurantSectionBlock';
import JustOnePageSiteFooter from './JustOnePageSiteFooter';
import SectionBackground from './SectionBackground';
import {
  forestFillGridPlacement,
  getSectionDesignKit,
  gridRowHeightForSection,
  isForestFillImage,
  isJardinsForestSection,
  kitCellAlignStyle,
  kitGridTrackStyle,
  kitGridItemSizeStyle,
  kitGridZIndex,
  siteUsesDesignKit,
} from '../utils/designKit';
import {
  resolveJardinsSectionPadding,
  shouldUseJardinsCompactHeader,
} from '../utils/jardinsResponsive';
import {
  containsJardinsDecor,
  replaceJardinsDecorInHtml,
  replaceJardinsDecorInPlainText,
  shouldUseJardinsPicto,
} from '../utils/jardinsPicto';
import JardinsCompactHeader from './JardinsCompactHeader';
import JardinsMobileBookingBar from './JardinsMobileBookingBar';
import '../styles/WebCreatorJardinsKit.css';
import AnimatedComponentWrapper from './AnimatedComponentWrapper';
import { SectionAnimationProvider } from './SectionAnimationContext';
import '../styles/webCreatorRichText.css';
import '../styles/webCreatorHoverEffects.css';
import '../styles/webCreatorMenuTabs.css';
import { resolveMenuTabAppearance } from '../utils/menuTabAppearance';
import {
  buildHoverEffectPresentation,
  resolveMenuTabHoverPresentation,
} from '../utils/mediaHoverEffects';
import '../styles/webCreatorAnimations.css';
import { usePageMenuScrolled } from '../hooks/usePageMenuScroll';
import { useOverlayMenuPull } from '../hooks/useOverlayMenuPull';
import { buildTextEffectPresentation } from '../utils/textComponentEffects';
import { resolveTextBlockTypographyStyle } from '../utils/textBlockTypography';
import {
  MENU_LAYOUT_COLUMNS,
  MENU_ROW_MIN_PX,
  menuGridItemStyle,
  menuLayoutRowCount,
  resolveLogoMenuPlacement,
  resolveTabPlacement,
} from '../utils/menuLayout';
import { useMenuBarLayoutDrag } from '../hooks/useMenuBarLayoutDrag';
import {
  SiteAmbientMusicProvider,
  AmbientMusicMenuControl,
  AmbientMusicFixedControl,
} from './SiteAmbientMusic';
import { hasAmbientMusic } from '../utils/ambientMusic';

function MenuView({
  comp,
  sections,
  interactive,
  viewport = 'desktop',
  checkoutSlug,
  menuLayoutEdit = false,
  onMenuLayoutChange,
  siteTypography,
}) {
  const menuProps = comp.props || {};
  const {
    logoUrl,
    logoPosition,
    tabsPosition,
    menuFreeLayout,
    backgroundColor,
    textColor,
    compactLogoPosition,
    compactMenuAlign,
    mobileMenuStyle,
    overlayFirstSection,
    scrollBackgroundColor,
    scrollBackgroundThreshold,
  } = menuProps;
  const overlayHero = overlayFirstSection === true;
  const scrollThreshold = Math.max(0, Number(scrollBackgroundThreshold) || 16);
  const menuScrolled = usePageMenuScrolled(overlayHero, scrollThreshold);
  const padding = resolveComponentPadding(comp.props);
  const menuTypographyStyle = resolveMenuTabTypographyStyle(menuProps, siteTypography);
  const menuItemTypographyStyle = menuTypographyStyle;
  const tabs = getMenuTabs(sections, comp, { viewport });
  const [iframeTarget, setIframeTarget] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const compact = viewport !== 'desktop';
  const useHamburger = compact && mobileMenuStyle !== 'tabs';
  const desktopFreeLayout = !compact && menuFreeLayout === true;
  const menuGridRef = useRef(null);

  const handleMenuPlacementChange = useCallback(
    (targetId, placement) => {
      onMenuLayoutChange?.({ target: targetId, placement });
    },
    [onMenuLayoutChange],
  );

  const { startLayoutDrag, draggingId, suppressClickRef } = useMenuBarLayoutDrag({
    enabled: menuLayoutEdit && desktopFreeLayout,
    gridRef: menuGridRef,
    onPlacementChange: handleMenuPlacementChange,
  });

  const layoutEditActive = menuLayoutEdit && desktopFreeLayout;

  const handleTabClick = (tab) => {
    if (suppressClickRef.current) return;
    if (tab.kind === 'section') {
      if (interactive) scrollToAnchor(tab.anchor);
      setMenuOpen(false);
      return;
    }
    if (tab.kind === 'button') {
      const linkType = tab.linkType || 'section';
      if (linkType === 'section') {
        if (interactive && tab.sectionAnchor) {
          scrollToAnchor(tab.sectionAnchor);
        }
        setMenuOpen(false);
        return;
      }
      if (linkType === 'publicPath') {
        const path = tab.publicPath || '/commande';
        if (interactive && checkoutSlug) {
          window.location.href = resolvePublicPath(checkoutSlug, path);
        }
        setMenuOpen(false);
        return;
      }
      if (linkType === 'url') {
        const url = normalizeExternalUrl(tab.url);
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
        setMenuOpen(false);
      }
      return;
    }
    if (tab.openMode === 'section') {
      if (interactive && tab.sectionAnchor) scrollToAnchor(tab.sectionAnchor);
      setMenuOpen(false);
      return;
    }
    if (tab.openMode === 'publicPath') {
      const path = tab.url || tab.publicPath || '/commande';
      if (interactive && checkoutSlug) {
        window.location.href = resolvePublicPath(checkoutSlug, path);
      }
      setMenuOpen(false);
      return;
    }
    const url = normalizeExternalUrl(tab.url);
    if (!url) return;
    if (tab.openMode === 'iframe') {
      setIframeTarget({ url, title: tab.label || 'Site externe' });
      setMenuOpen(false);
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
    setMenuOpen(false);
  };

  const justifyTabs =
    tabsPosition === 'center'
      ? 'justify-center'
      : tabsPosition === 'right'
        ? 'justify-end'
        : 'justify-start';

  const tabsCentered = !compact && tabsPosition === 'center';

  const handleLogoClick = (e) => {
    if (suppressClickRef.current) return;
    if (!interactive) return;
    e.stopPropagation();
    scrollToPageTop();
    setMenuOpen(false);
  };

  const logoBlock = (
    <button
      type="button"
      onClick={handleLogoClick}
      className={`inline-flex items-center p-0 border-0 bg-transparent ${
        interactive
          ? 'cursor-pointer hover:opacity-80 transition-opacity'
          : 'cursor-default pointer-events-none'
      }`}
      aria-label="Retour en haut de la page"
      tabIndex={interactive ? 0 : -1}
    >
      {logoUrl ? (
        <img src={logoUrl} alt="" className="max-h-10 max-w-[140px] object-contain" />
      ) : (
        <span className="font-semibold opacity-60" style={menuItemTypographyStyle}>
          Logo
        </span>
      )}
    </button>
  );

  const renderTabButton = (tab, layoutStyle) => {
    const tabKey = `${tab.kind}:${tab.id}`;
    const tabHover = resolveMenuTabHoverPresentation(menuProps, tabKey, { textColor });
    const tabLook = resolveMenuTabAppearance(menuProps, tabKey);
    const tabTextStyle = { ...menuItemTypographyStyle, ...tabLook.style };
    return (
    <button
      key={tabKey}
      type="button"
      style={{ ...layoutStyle, ...tabHover.style, ...tabTextStyle }}
      className={`px-3 py-2 text-left min-w-0 relative ${
        tab.kind === 'link' || tab.kind === 'button' ? 'inline-flex items-center gap-1' : ''
      } ${useHamburger && menuOpen ? 'w-full' : ''} ${tabLook.className} ${tabHover.selfClass}`.trim()}
      onClick={() => handleTabClick(tab)}
    >
      {tab.label}
      {tab.kind === 'link' && tab.openMode === 'iframe' ? (
        <span className="text-[10px] opacity-60" aria-hidden>
          ⛶
        </span>
      ) : null}
      {tab.kind === 'link' && tab.openMode === 'publicPath' ? (
        <span className="text-[10px] opacity-60" aria-hidden>
          →
        </span>
      ) : null}
    </button>
    );
  };

  const { main: mainTabs, right: rightPinnedTabs } = splitMenuTabsByBarAlign(tabs, menuProps);

  const renderTabList = (list) => list.map((tab) => renderTabButton(tab));

  const wrapMenuLayoutCell = (targetId, placement, node) => {
    const style = menuGridItemStyle(placement);
    if (!layoutEditActive) {
      return (
        <div key={targetId} className="flex min-w-0" style={style}>
          {node}
        </div>
      );
    }
    const dragging = draggingId === targetId;
    return (
      <div
        key={targetId}
        className={`flex min-w-0 touch-none select-none ${
          dragging
            ? 'z-20 ring-2 ring-primary/70 ring-offset-1 rounded-md bg-primary/5'
            : 'hover:ring-1 hover:ring-primary/30 rounded-md'
        } cursor-grab active:cursor-grabbing`}
        style={style}
        data-menu-layout-item
        onPointerDown={(e) => startLayoutDrag(e, targetId, placement)}
      >
        {node}
      </div>
    );
  };

  const menuGridRowCount = menuLayoutRowCount(menuProps, tabs, {
    editMode: layoutEditActive,
  });
  const menuGridStyle = {
    display: 'grid',
    gridTemplateColumns: `repeat(${MENU_LAYOUT_COLUMNS}, minmax(0, 1fr))`,
    gridTemplateRows: `repeat(${menuGridRowCount}, minmax(${MENU_ROW_MIN_PX}px, auto))`,
    alignItems: 'center',
    columnGap: 0,
    rowGap: 2,
    width: '100%',
    minHeight: Math.max(52, menuGridRowCount * MENU_ROW_MIN_PX),
  };

  const hamburgerBtn = (
    <button
      type="button"
      className="p-2 rounded-lg hover:bg-black/5 shrink-0"
      aria-expanded={menuOpen}
      aria-label={menuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
      onClick={() => setMenuOpen((v) => !v)}
    >
      <Icon name={menuOpen ? 'X' : 'Menu'} size={22} />
    </button>
  );

  const compactLogoPos = compactLogoPosition || logoPosition || 'left';
  const compactMenuSide = compactMenuAlign || 'right';

  const logoJustify =
    compactLogoPos === 'center'
      ? 'justify-center'
      : compactLogoPos === 'right'
        ? 'justify-end'
        : 'justify-start';

  const solidMenuSurface = !overlayHero || menuScrolled;
  const menuBackgroundFill = solidMenuSurface
    ? resolveColorFillStyle(
        menuScrolled && scrollBackgroundColor ? scrollBackgroundColor : backgroundColor,
        '#ffffff',
      )
    : { backgroundColor: 'transparent' };

  return (
    <>
      <div
        className={`w-full h-full box-border relative transition-[background-color,border-color,box-shadow] duration-300 ${
          solidMenuSurface
            ? 'border-b border-black/5 shadow-sm'
            : 'border-b border-transparent shadow-none'
        } ${overlayHero && menuScrolled ? 'backdrop-blur-md' : ''}`}
        style={{
          ...menuBackgroundFill,
          ...resolveTextFillStyle(textColor, '#111'),
          ...padding,
          ...(menuTypographyStyle.fontFamily
            ? { fontFamily: menuTypographyStyle.fontFamily }
            : {}),
        }}
      >
        {compact && useHamburger ? (
          <>
            <div className="flex items-center gap-1 w-full min-h-[52px]">
              {compactMenuSide === 'left' ? hamburgerBtn : null}
              <div className={`flex min-w-0 flex-1 ${logoJustify}`}>{logoBlock}</div>
              <AmbientMusicMenuControl />
              {compactMenuSide === 'right' ? hamburgerBtn : null}
            </div>
            {menuOpen ? (
              <nav className="flex flex-col gap-0.5 border-t border-black/10 pt-2 pb-3 z-40">
                {renderTabList(mainTabs)}
                {rightPinnedTabs.length ? (
                  <div className="flex flex-col gap-0.5 border-t border-black/10 mt-1 pt-2">
                    {renderTabList(rightPinnedTabs)}
                  </div>
                ) : null}
              </nav>
            ) : null}
          </>
        ) : compact ? (
          <div className="flex flex-col gap-3 py-1">
            <div className={`flex w-full items-center gap-2 ${logoJustify}`}>
              <div className="flex min-w-0 flex-1 justify-inherit">{logoBlock}</div>
              <AmbientMusicMenuControl className="shrink-0" />
            </div>
            <nav className="flex flex-wrap gap-2 w-full justify-center">
              {renderTabList(tabs)}
            </nav>
          </div>
        ) : desktopFreeLayout ? (
          <div className="flex w-full items-center gap-2 min-w-0">
            <nav
              ref={menuGridRef}
              className={`z-[1] flex-1 min-w-0 ${layoutEditActive ? 'wc-menu-layout-edit' : ''}`}
              style={menuGridStyle}
              aria-label="Navigation principale"
            >
              {wrapMenuLayoutCell(
                'logo',
                resolveLogoMenuPlacement(menuProps),
                logoBlock,
              )}
              {mainTabs.map((tab, index) => {
                const key = `${tab.kind}:${tab.id}`;
                const placement = resolveTabPlacement(key, index, mainTabs.length, menuProps);
                return wrapMenuLayoutCell(key, placement, renderTabButton(tab));
              })}
            </nav>
            {rightPinnedTabs.length ? (
              <div className="flex flex-wrap items-center gap-1 shrink-0 z-[2] pr-0.5">
                {renderTabList(rightPinnedTabs)}
                <AmbientMusicMenuControl />
              </div>
            ) : (
              <AmbientMusicMenuControl className="shrink-0 z-[2]" />
            )}
          </div>
        ) : (
          <div className="relative flex items-center min-h-[52px]">
            <div
              className={`flex min-w-0 shrink-0 z-[1] ${
                logoPosition === 'right'
                  ? 'ml-auto'
                  : logoPosition === 'center'
                    ? 'mx-auto'
                    : ''
              }`}
            >
              {logoBlock}
            </div>
            {tabsCentered ? (
              <>
                <nav className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[2] flex flex-wrap gap-2 justify-center max-w-[min(100%,720px)]">
                  {renderTabList(mainTabs)}
                </nav>
                {rightPinnedTabs.length ? (
                  <nav className="absolute right-0 top-1/2 -translate-y-1/2 z-[2] flex flex-wrap items-center gap-1 justify-end pr-0.5">
                    {renderTabList(rightPinnedTabs)}
                    <AmbientMusicMenuControl />
                  </nav>
                ) : (
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 z-[2]">
                    <AmbientMusicMenuControl />
                  </div>
                )}
              </>
            ) : (
              <nav className="z-[1] flex flex-1 flex-wrap items-center gap-2 min-w-0">
                <div className={`flex flex-wrap gap-2 min-w-0 flex-1 ${justifyTabs}`}>
                  {renderTabList(mainTabs)}
                </div>
                {rightPinnedTabs.length ? (
                  <div className="flex flex-wrap items-center gap-1 shrink-0 ml-2">
                    {renderTabList(rightPinnedTabs)}
                    <AmbientMusicMenuControl />
                  </div>
                ) : (
                  <AmbientMusicMenuControl className="shrink-0 ml-2" />
                )}
              </nav>
            )}
          </div>
        )}
      </div>
      {iframeTarget ? (
        <ExternalSiteModal
          url={iframeTarget.url}
          title={iframeTarget.title}
          onClose={() => setIframeTarget(null)}
        />
      ) : null}
    </>
  );
}

function kitTextClass(kitStyle) {
  if (!kitStyle) return 'wc-kit-text';
  return `wc-kit-text wc-kit-text--${kitStyle}`;
}

function kitBtnClass(kitStyle) {
  if (!kitStyle) return '';
  return `wc-kit-btn wc-kit-btn--${kitStyle}`;
}

function kitImgClass(kitStyle) {
  if (!kitStyle) return '';
  return `wc-kit-img wc-kit-img--${kitStyle}`;
}

function TextView({ comp, siteTypography, interactive, checkoutSlug, site }) {
  const {
    content,
    contentFormat,
    inheritSiteFont,
    fontSize,
    fontWeight,
    color,
    textAlign,
    kitStyle,
    linkType,
    sectionAnchor,
    url,
    publicPath,
  } = comp.props || {};
  const isHtml =
    contentFormat === 'html' || (content && /<[a-z][\s\S]*>/i.test(content));

  const fontStyle = (() => {
    if (kitStyle) return {};
    if (inheritSiteFont !== false) return resolveInheritedFontStyle(siteTypography);
    return {
      fontFamily: resolveFontFamilyChoice(
        comp.props?.textFontFamily || 'system',
        siteTypography,
      ),
    };
  })();

  const textFx = buildTextEffectPresentation(comp.props);
  const contentHover = buildHoverEffectPresentation(comp.props, 'content');
  const className = `${kitTextClass(kitStyle)} ${textFx.className}`.trim();
  const textFxStyle = textFx.style;
  const blockTypographyStyle = resolveTextBlockTypographyStyle(comp.props);

  const wrapContentHover = (node) => {
    if (contentHover.effect === 'none') return node;
    if (contentHover.wrapClass) {
      return (
        <div className={`${contentHover.wrapClass} w-full h-full`} style={contentHover.style}>
          <div className={`w-full h-full ${contentHover.targetClass}`.trim()}>{node}</div>
        </div>
      );
    }
    return (
      <div
        className={`w-full h-full relative ${contentHover.selfClass}`.trim()}
        style={contentHover.style}
      >
        {node}
      </div>
    );
  };

  const rawHtml = content || '<p>Texte</p>';
  const htmlForRender =
    isHtml && shouldUseJardinsPicto({ kitStyle, site })
      ? replaceJardinsDecorInHtml(rawHtml)
      : rawHtml;

  const inner = isHtml ? (
    <div
      className={`web-creator-rich-text ${className}${kitStyle ? ' wc-kit-text-inner' : ''}`.trim()}
      style={{ ...fontStyle, ...blockTypographyStyle, ...textFxStyle }}
      dangerouslySetInnerHTML={{ __html: htmlForRender }}
    />
  ) : (
    <div
      className={
        kitStyle ? kitTextClass(kitStyle) : `w-full h-full ${textFx.className}`.trim()
      }
      style={{
        fontSize: `${fontSize || 16}px`,
        fontWeight: fontWeight || 'normal',
        color: color || '#111827',
        textAlign: textAlign || 'left',
        ...fontStyle,
        ...blockTypographyStyle,
        ...textFxStyle,
      }}
    >
      {content || 'Texte'}
    </div>
  );

  const linkWrapClass = kitStyle ? 'block w-full h-auto' : 'block w-full h-full';

  if (linkType === 'section' && sectionAnchor) {
    return (
      <a
        href={`#${sectionAnchor}`}
        className={linkWrapClass}
        onClick={(e) => {
          if (interactive) {
            e.preventDefault();
            scrollToAnchor(sectionAnchor);
          }
        }}
      >
        {wrapContentHover(inner)}
      </a>
    );
  }
  if (linkType === 'url' && url) {
    return (
      <a href={url} className={linkWrapClass} target="_blank" rel="noreferrer">
        {wrapContentHover(inner)}
      </a>
    );
  }
  if (linkType === 'publicPath' && publicPath && checkoutSlug) {
    const href = resolvePublicPath(checkoutSlug, publicPath);
    return (
      <a href={href} className={linkWrapClass}>
        {wrapContentHover(inner)}
      </a>
    );
  }

  return wrapContentHover(inner);
}

function ImageView({ comp }) {
  const {
    src,
    objectFit,
    borderRadius,
    shape,
    minHeight,
    kitStyle,
    alt,
    objectPosition,
    imageZoom,
  } = comp.props || {};
  const radius = kitStyle ? 0 : resolveShapeRadius(shape, borderRadius);
  const isCircle = !kitStyle && shape === 'circle';
  const kitClass = kitImgClass(kitStyle);
  const hoverFx = buildHoverEffectPresentation(comp.props, 'image');
  const zoomRaw = Number(imageZoom);
  const zoom = Number.isFinite(zoomRaw) ? Math.max(0.5, Math.min(2.5, zoomRaw / 100)) : 1;
  const pos = objectPosition || (kitStyle === 'jardins-fill' ? 'center 53%' : 'center');
  const fit = objectFit || 'fill';
  const clipRadius =
    hoverFx.effect !== 'none' && !kitStyle ? 0 : isCircle ? '50%' : `${radius}px`;

  const imgStyle = kitStyle
    ? {
        objectFit: fit,
        objectPosition: pos,
        borderRadius: 0,
        width: '100%',
        height: '100%',
        minHeight: 0,
        maxHeight: '100%',
        display: 'block',
        transform: zoom !== 1 ? `scale(${zoom})` : undefined,
        transformOrigin: pos,
      }
    : {
        objectFit: fit,
        objectPosition: pos,
        width: '100%',
        height: '100%',
        minHeight: 0,
        maxHeight: '100%',
        display: 'block',
        transform: zoom !== 1 ? `scale(${zoom})` : undefined,
        transformOrigin: pos,
      };

  const placeholder = (
    <div
      className="w-full h-full min-h-[120px] bg-neutral-100 flex items-center justify-center text-sm text-neutral-400"
      style={{ borderRadius: clipRadius }}
    >
      Image
    </div>
  );

  const frameClass = isCircle
    ? 'w-full max-w-full aspect-square mx-auto overflow-hidden'
    : kitStyle
      ? 'h-full w-full min-h-0 overflow-hidden'
      : 'w-full h-full min-h-0 overflow-hidden flex-1';

  const imgNode = src ? (
    <div className={frameClass} style={{ borderRadius: clipRadius }}>
      <img
        src={src}
        alt={alt || ''}
        className={`h-full w-full ${kitClass} ${hoverFx.targetClass}`.trim()}
        style={imgStyle}
      />
    </div>
  ) : (
    placeholder
  );

  const outerClass = kitStyle
    ? 'h-full w-full min-h-0 flex flex-col'
    : 'w-full h-full min-h-0 flex flex-col';

  if (hoverFx.effect === 'none' || !src) {
    return <div className={outerClass}>{imgNode}</div>;
  }

  return (
    <div className={outerClass}>
      <div
        className={`h-full w-full min-h-0 overflow-hidden ${hoverFx.wrapClass}`.trim()}
        style={{
          borderRadius: clipRadius || undefined,
          ...hoverFx.style,
        }}
      >
        {imgNode}
      </div>
    </div>
  );
}

function IconView({ comp }) {
  const { iconName, size, color, strokeWidth, align } = comp.props || {};
  const iconSize = Math.max(12, Math.min(256, Number(size) || 32));
  const name = normalizeIconName(iconName);
  const strokeColor = resolveIconStrokeColor(color, '#2563eb');
  const alignClass =
    align === 'left' ? 'justify-start' : align === 'right' ? 'justify-end' : 'justify-center';

  return (
    <div className={`w-full h-full flex items-center ${alignClass}`}>
      <Icon
        name={name}
        size={iconSize}
        color={strokeColor}
        strokeWidth={Math.max(1, Math.min(4, Number(strokeWidth) || 2))}
      />
    </div>
  );
}

function isCommerceInline(comp) {
  return comp?.props?.displayMode === 'inline';
}

function CommerceActionButton({ comp, interactive, onCommerceNavigate, action, siteTypography }) {
  const { label, shape } = comp.props || {};
  const { button: btnStyle, text: textStyle } = resolveButtonStyle(comp.props, siteTypography);

  const handleClick = () => {
    if (!interactive) return;
    onCommerceNavigate?.(action);
  };

  return (
    <button
      type="button"
      className={`px-6 py-3 font-medium shadow-sm ${
        comp.props?.width === 'full' ? 'w-full' : 'w-full sm:w-auto'
      } ${shape === 'circle' ? 'p-0 min-w-[48px] min-h-[48px]' : ''}`}
      style={btnStyle}
      onClick={handleClick}
    >
      <span style={textStyle}>{label || (action === 'click-collect' ? 'Click & collect' : 'Réserver')}</span>
    </button>
  );
}

function ButtonView({ comp, interactive, checkoutSlug, siteTypography }) {
  const { label, linkType, sectionAnchor, url, shape, kitStyle, publicPath } = comp.props || {};
  const { button: btnStyle, text: textStyle } = resolveButtonStyle(comp.props, siteTypography);
  const kitClass = kitBtnClass(kitStyle);

  const handleClick = () => {
    if (!interactive) return;
    if (linkType === 'url' && url) window.open(url, '_blank', 'noopener,noreferrer');
    else if (linkType === 'publicPath' && publicPath && checkoutSlug) {
      window.location.href = resolvePublicPath(checkoutSlug, publicPath);
    } else if (sectionAnchor) scrollToAnchor(sectionAnchor);
  };

  const hoverFx = buildHoverEffectPresentation(comp.props, 'button');
  const applyButtonHover = hoverFx.effect !== 'none';

  const className = [
    kitClass || 'px-6 py-3 font-medium shadow-sm',
    comp.props?.width === 'full' ? 'w-full' : 'w-full sm:w-auto',
    shape === 'circle' && !kitStyle ? 'p-0 min-w-[48px] min-h-[48px]' : '',
    applyButtonHover ? hoverFx.selfClass : '',
  ]
    .filter(Boolean)
    .join(' ');

  const displayLabel = label || 'Bouton';
  const isJardinsKitBtn = String(kitStyle || '').startsWith('jardins-');
  const labelInner =
    isJardinsKitBtn && containsJardinsDecor(displayLabel) ? (
      <span
        style={kitStyle ? undefined : textStyle}
        dangerouslySetInnerHTML={{ __html: replaceJardinsDecorInPlainText(displayLabel) }}
      />
    ) : (
      <span style={kitStyle ? undefined : textStyle}>{displayLabel}</span>
    );

  return (
    <button
      type="button"
      className={className}
      style={
        kitStyle && !applyButtonHover
          ? undefined
          : { ...btnStyle, ...(applyButtonHover ? hoverFx.style : {}) }
      }
      onClick={handleClick}
    >
      {labelInner}
    </button>
  );
}

function DropdownView({ comp, interactive, siteTypography }) {
  const {
    placeholder,
    options = [],
    width,
  } = comp.props || {};
  const { trigger, text, menu, menuItem } = resolveDropdownStyle(comp.props, siteTypography);
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const rootRef = useRef(null);

  const selected = options.find((o) => o.id === selectedId);
  const displayLabel = selected?.label || placeholder || 'Sélectionner…';

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const handleSelect = (opt) => {
    setSelectedId(opt.id);
    setOpen(false);
    if (!interactive) return;
    if (opt.linkType === 'url' && opt.url) {
      window.open(normalizeExternalUrl(opt.url), '_blank', 'noopener,noreferrer');
    } else if (opt.sectionAnchor) {
      scrollToAnchor(opt.sectionAnchor);
    }
  };

  return (
    <div
      ref={rootRef}
      className={`relative ${width === 'full' ? 'w-full' : 'w-full max-w-md'}`}
    >
      <button
        type="button"
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 font-medium shadow-sm transition-smooth"
        style={trigger}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className="truncate text-left" style={text}>
          {displayLabel}
        </span>
        <Icon
          name="ChevronDown"
          size={16}
          className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          color={text?.color || 'currentColor'}
        />
      </button>

      {open && options.length > 0 ? (
        <ul
          className="absolute left-0 right-0 top-[calc(100%+4px)] z-50 rounded-lg shadow-lg overflow-hidden max-h-56 overflow-y-auto"
          style={menu}
          role="listbox"
        >
          {options.map((opt) => (
            <li key={opt.id}>
              <button
                type="button"
                role="option"
                aria-selected={selectedId === opt.id}
                className={`w-full text-left px-3 py-2.5 text-sm transition-colors hover:bg-black/5 ${
                  selectedId === opt.id ? 'bg-black/[0.04] font-medium' : ''
                }`}
                style={menuItem}
                onClick={() => handleSelect(opt)}
              >
                {opt.label || 'Option'}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function DividerView({ comp }) {
  const {
    label,
    lineColor,
    lineStyle = 'solid',
    thickness = 1,
    labelColor,
    width = 'full',
    align = 'center',
  } = comp.props || {};
  const lineCss = resolveColorFillCss(lineColor, '#e5e7eb');
  const labelCss = resolveColorFillCss(labelColor, '#64748b');
  const lineStyleObj = {
    borderTopWidth: `${thickness}px`,
    borderTopStyle: lineStyle,
    borderTopColor: lineCss,
    flex: 1,
    minWidth: 0,
  };

  if (!label || !String(label).trim()) {
    return (
      <div
        className={`flex w-full items-center ${width === 'narrow' ? 'max-w-md mx-auto' : ''}`}
        style={{ borderTop: `${thickness}px ${lineStyle} ${lineCss}` }}
        role="separator"
      />
    );
  }

  const justify =
    align === 'left' ? 'justify-start' : align === 'right' ? 'justify-end' : 'justify-center';

  return (
    <div
      className={`flex w-full items-center gap-3 ${justify} ${width === 'narrow' ? 'max-w-md mx-auto' : ''}`}
      role="separator"
    >
      {align !== 'left' ? <div style={lineStyleObj} aria-hidden /> : null}
      <span className="shrink-0 text-xs font-medium uppercase tracking-wider" style={{ color: labelCss }}>
        {label}
      </span>
      {align !== 'right' ? <div style={lineStyleObj} aria-hidden /> : null}
    </div>
  );
}

function BadgeView({ comp }) {
  const { text, align = 'center' } = comp.props || {};
  const { badge, text: textStyle } = resolveBadgeStyle(comp.props);
  const alignClass =
    align === 'left' ? 'justify-start' : align === 'right' ? 'justify-end' : 'justify-center';

  return (
    <div className={`flex w-full h-full items-center ${alignClass}`}>
      <span style={badge}>
        <span style={textStyle}>{text || 'Badge'}</span>
      </span>
    </div>
  );
}

function QuoteView({ comp }) {
  const {
    quote,
    authorName,
    authorRole,
    avatarUrl,
    accentColor,
    showQuoteMark = true,
    cardStyle = true,
    align = 'left',
  } = comp.props || {};
  const accent = resolveColorFillCss(accentColor, '#2563eb');
  const textAlign = align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left';
  const flexAlign =
    align === 'center' ? 'justify-center' : align === 'right' ? 'justify-end' : 'justify-start';

  return (
    <figure
      className={`w-full h-full flex flex-col justify-center ${cardStyle ? 'rounded-xl border border-stone-200/80 bg-white/60 p-5 shadow-sm' : ''} ${textAlign}`}
    >
      {showQuoteMark ? (
        <span
          className="mb-2 block text-3xl leading-none font-serif opacity-40 select-none"
          style={{ color: accent }}
          aria-hidden
        >
          "
        </span>
      ) : null}
      <blockquote className="text-base leading-relaxed text-stone-700 italic">
        {quote || 'Votre citation…'}
      </blockquote>
      {(authorName || authorRole || avatarUrl) ? (
        <figcaption className={`mt-4 flex items-center gap-3 ${flexAlign}`}>
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt=""
              className="h-10 w-10 shrink-0 rounded-full object-cover ring-2 ring-white"
            />
          ) : null}
          <div className={textAlign}>
            {authorName ? (
              <span className="block text-sm font-semibold text-stone-900">{authorName}</span>
            ) : null}
            {authorRole ? (
              <span className="block text-xs text-stone-500">{authorRole}</span>
            ) : null}
          </div>
        </figcaption>
      ) : null}
    </figure>
  );
}

export function RenderComponent({
  comp,
  sections,
  interactive = false,
  editorViewport,
  siteTypography,
  liveHubupFetch = false,
  checkoutSlug,
  onProductBuy,
  commerceCapabilities,
  onCommerceNavigate,
  site,
  onOrderComplete,
  menuLayoutEdit = false,
  onMenuLayoutChange,
  commerceSlotHeightPx = 0,
}) {
  const compBg = resolveComponentBackground(comp.props);
  const paddingStyle = resolveComponentPadding(comp.props);
  const menuViewport = editorViewport || 'desktop';
  const inner = (() => {
    switch (comp.type) {
      case 'restaurantPage':
        return (
          <RestaurantExempleView
            props={comp.props}
            site={site}
            slug={site?.slug || checkoutSlug}
            interactive={interactive && Boolean(site?.slug || checkoutSlug)}
            commandeUrl={
              site?.slug || checkoutSlug
                ? `/lp/${encodeURIComponent(site?.slug || checkoutSlug)}/commande`
                : undefined
            }
          />
        );
      case 'restaurantSection':
        return (
          <RestaurantSectionBlock
            props={comp.props}
            site={site}
            slug={site?.slug || checkoutSlug}
            interactive={interactive && Boolean(site?.slug || checkoutSlug)}
            commandeUrl={
              site?.slug || checkoutSlug
                ? `/lp/${encodeURIComponent(site?.slug || checkoutSlug)}/commande`
                : undefined
            }
          />
        );
      case 'menu':
        return (
          <MenuView
            comp={comp}
            sections={sections}
            interactive={interactive}
            viewport={menuViewport}
            checkoutSlug={site?.slug || checkoutSlug}
            menuLayoutEdit={menuLayoutEdit}
            onMenuLayoutChange={onMenuLayoutChange}
            siteTypography={site?.typography}
          />
        );
      case 'text':
        return (
          <TextView
            comp={comp}
            siteTypography={siteTypography}
            interactive={interactive}
            checkoutSlug={site?.slug || checkoutSlug}
            site={site}
          />
        );
      case 'image':
        return <ImageView comp={comp} />;
      case 'button':
        return (
          <ButtonView
            comp={comp}
            interactive={interactive}
            checkoutSlug={site?.slug || checkoutSlug}
            siteTypography={siteTypography}
          />
        );
      case 'clickAndCollect':
        if (!commerceCapabilities?.clickAndCollect) {
          return <ButtonView comp={comp} interactive={false} />;
        }
        if (isCommerceInline(comp)) {
          const slug = site?.slug || checkoutSlug;
          return (
            <ClickCollectPublicView
              embedded
              slug={slug}
              site={site}
              siteTitle={site?.title}
              items={site?.clickAndCollect?.items || []}
              display={comp.props?.menuPage}
              interactive={interactive && Boolean(slug)}
              onOrderComplete={onOrderComplete}
            />
          );
        }
        return (
          <CommerceActionButton
            comp={comp}
            interactive={interactive}
            action="click-collect"
            onCommerceNavigate={onCommerceNavigate}
            siteTypography={siteTypography}
          />
        );
      case 'reservation':
        if (!commerceCapabilities?.reservation) {
          return <ButtonView comp={comp} interactive={false} />;
        }
        if (isCommerceInline(comp)) {
          const slug = site?.slug || checkoutSlug;
          return (
            <ReservationPublicView
              embedded
              slug={slug}
              site={site}
              siteTitle={site?.title}
              reservation={site?.reservation || {}}
              variant={comp.props?.theme === 'restaurant' ? 'restaurant' : 'default'}
              layoutMode={comp.props?.layoutMode}
              reservationCopy={comp.props?.reservationCopy}
              interactive={interactive && Boolean(slug)}
              commerceExpandCompId={comp.id}
              commerceExpandSlotHeightPx={commerceSlotHeightPx}
            />
          );
        }
        return (
          <CommerceActionButton
            comp={comp}
            interactive={interactive}
            action="reservation"
            onCommerceNavigate={onCommerceNavigate}
            siteTypography={siteTypography}
          />
        );
      case 'dropdown':
        return (
          <DropdownView comp={comp} interactive={interactive} siteTypography={siteTypography} />
        );
      case 'divider':
        return <DividerView comp={comp} />;
      case 'badge':
        return <BadgeView comp={comp} />;
      case 'quote':
        return <QuoteView comp={comp} />;
      case 'icon':
        return <IconView comp={comp} />;
      case 'product':
        return (
          <ProductCardView
            comp={comp}
            checkoutEnabled={Boolean(checkoutSlug)}
            onBuy={onProductBuy}
          />
        );
      case 'cardFeature':
        return (
          <CardFeatureView
            comp={comp}
            interactive={interactive}
            checkoutSlug={checkoutSlug}
          />
        );
      case 'cardIllustration':
        return <CardIllustrationView comp={comp} />;
      case 'cardSplit':
        return (
          <CardSplitView
            comp={comp}
            interactive={interactive}
            checkoutSlug={checkoutSlug}
            siteTypography={siteTypography}
            editorViewport={editorViewport}
          />
        );
      case 'hubupQr':
        return <HubupQrView comp={comp} />;
      case 'hubupNotifications':
        return <HubupNotificationsView comp={comp} liveFetch={liveHubupFetch} />;
      default:
        return null;
    }
  })();

  if (comp.type === 'menu' || comp.type === 'restaurantPage' || comp.type === 'restaurantSection') {
    return inner;
  }
  if (
    (comp.type === 'clickAndCollect' || comp.type === 'reservation') &&
    isCommerceInline(comp)
  ) {
    return (
      <div className="wc-commerce-embedded w-full min-h-0 overflow-visible box-border">
        {inner}
      </div>
    );
  }

  const hubupContained = comp.type === 'hubupQr' || comp.type === 'hubupNotifications';
  const isImageComp = comp.type === 'image';
  const isCardComp =
    comp.type === 'cardFeature' ||
    comp.type === 'cardIllustration' ||
    comp.type === 'cardSplit';
  const kitImage = isImageComp && comp.props?.kitStyle;
  const fillHeight = isImageComp || isCardComp || kitImage;

  const kitSection = Boolean(comp.props?.kitStyle);
  const kitAlign = kitCellAlignStyle(comp.props, comp.type);
  const useKitCell = kitAlign || kitImage || (kitSection && (comp.type === 'text' || comp.type === 'button'));
  const padded = (
    <div
      className={`w-full box-border ${
        fillHeight ? 'h-full min-h-0 max-h-full overflow-hidden' : 'h-auto min-h-0'
      } ${useKitCell ? 'wc-kit-cell' : ''}`}
      style={
        kitAlign
          ? { ...paddingStyle, ...kitAlign }
          : fillHeight
            ? {
                ...paddingStyle,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 0,
              }
            : paddingStyle
      }
    >
      {inner}
    </div>
  );

  if (Object.keys(compBg).length === 0) return padded;
  return (
    <div className="w-full h-full rounded-sm overflow-hidden box-border" style={compBg}>
      {padded}
    </div>
  );
}

function SectionGrid({
  section,
  sections,
  viewport,
  interactive,
  siteTypography,
  liveHubupFetch,
  checkoutSlug,
  onProductBuy,
  commerceCapabilities,
  onCommerceNavigate,
  site,
  onOrderComplete,
  playAnimations = true,
  editorMode,
  jardinsFluidScale = 1,
}) {
  const { totalExtraPx, CommerceSectionExpandProvider } = useCommerceSectionExpandRegistry();
  const heroViewportFill = isJardinsHeroViewportFill(section, viewport);
  const sorted = sortComponentsByGrid(section.components, viewport).filter(
    (c) => !isComponentHidden(c, viewport),
  );
  const forestSection = isJardinsForestSection(section);
  const forestDesktopBleed = forestSection && viewport === 'desktop';
  const totalGridRows = resolveSectionTotalGridRows(section, viewport, section.components);
  const sectionMinRows = resolveSectionMinGridRows(section, viewport);
  const kitRowH = (gridRowHeightForSection(section) || GRID_ROW_HEIGHT) * jardinsFluidScale;
  const isKit = Boolean(section?.designKit);
  const forestGridHeightPx = forestDesktopBleed ? totalGridRows * kitRowH : null;
  const userFixedHeightPx = resolveSectionFixedHeightPx(
    section,
    viewport,
    totalGridRows,
    kitRowH,
  );
  const sectionGridHeightPx = userFixedHeightPx ?? forestGridHeightPx;
  const baseSectionHeightPx =
    sectionGridHeightPx ?? (totalGridRows > 0 ? totalGridRows * kitRowH : null);
  const expandedSectionHeightPx =
    baseSectionHeightPx != null && totalExtraPx > 0
      ? baseSectionHeightPx + totalExtraPx
      : baseSectionHeightPx;

  if (shouldUseJardinsCompactHeader(section, viewport)) {
    return (
      <JardinsCompactHeader
        section={section}
        interactive={interactive}
        checkoutSlug={checkoutSlug}
        editorMode={editorMode}
      />
    );
  }

  return (
    <CommerceSectionExpandProvider>
    <div
      className={`w-full grid gap-0 ${isKit ? 'wc-kit-section-grid' : ''}`}
      data-commerce-section-expanded={totalExtraPx > 0 ? 'true' : undefined}
      style={{
        gridTemplateColumns: `repeat(${GRID_COLUMNS}, minmax(0, 1fr))`,
        ...kitGridTrackStyle(section, totalGridRows, kitRowH, viewport, {
          heroViewportFill,
        }),
        ...(heroViewportFill
          ? { minHeight: '100%', height: '100%' }
          : expandedSectionHeightPx != null
            ? {
                minHeight: expandedSectionHeightPx,
                height: totalExtraPx > 0 ? 'auto' : expandedSectionHeightPx,
              }
            : { minHeight: sectionMinRows > 0 ? sectionMinRows * kitRowH : undefined }),
      }}
    >
      {sorted.map((comp) => {
        const rawPlacement = getGridPlacement(comp, viewport);
        const placement =
          forestDesktopBleed && isForestFillImage(comp, section)
            ? forestFillGridPlacement(rawPlacement, totalGridRows)
            : rawPlacement;
        const rowSpan = placement.rowSpan ?? 1;
        const hubupType = comp.type === 'hubupQr' || comp.type === 'hubupNotifications';
        const restaurantFullBleed =
          comp.type === 'restaurantPage' || comp.type === 'restaurantSection';
        const commerceEmbedded =
          (comp.type === 'clickAndCollect' || comp.type === 'reservation') &&
          isCommerceInline(comp);
        const commerceExpanded = commerceEmbedded && totalExtraPx > 0;
        const commerceSlotHeightPx = (rowSpan ?? 1) * kitRowH;
        const commerceInline = restaurantFullBleed || commerceEmbedded;
        return (
          <div
            key={comp.id}
            data-commerce-slot={commerceEmbedded ? 'true' : undefined}
            className={
              hubupType
                ? 'min-h-0 max-h-full overflow-hidden'
                : commerceExpanded
                  ? 'min-h-0 overflow-visible w-full h-auto'
                  : commerceEmbedded
                  ? 'min-h-0 max-h-full h-full overflow-hidden w-full'
                  : commerceInline
                    ? 'min-h-0 overflow-visible w-full'
                  : isKit
                    ? `wc-kit-grid-item wc-kit-grid-item--${comp.type}${
                        comp.props?.kitStyle
                          ? ` wc-kit-grid-item--kit-${comp.props.kitStyle}`
                          : ''
                      } min-h-0`
                    : undefined
            }
            style={{
              ...gridStyle(placement),
              ...componentStackStyle(comp),
              ...(isKit
                ? { zIndex: kitGridZIndex(comp, getComponentLayout(comp)) }
                : {}),
              ...(comp.type === 'restaurantPage' || comp.type === 'restaurantSection'
                ? {}
                : kitGridItemSizeStyle(section, comp, rowSpan, kitRowH, { hubupType, viewport })),
              ...(comp.type === 'restaurantPage' || comp.type === 'restaurantSection'
                ? { overflow: 'visible', width: '100%' }
                : {}),
            }}
          >
            <AnimatedComponentWrapper
              comp={comp}
              section={section}
              viewport={viewport}
              disabled={!playAnimations}
            >
              <RenderComponent
                comp={comp}
                sections={sections}
                interactive={interactive}
                siteTypography={siteTypography}
                liveHubupFetch={liveHubupFetch}
                checkoutSlug={checkoutSlug}
                onProductBuy={onProductBuy}
                commerceCapabilities={commerceCapabilities}
                onCommerceNavigate={onCommerceNavigate}
                site={site}
                onOrderComplete={onOrderComplete}
                commerceSlotHeightPx={commerceEmbedded ? commerceSlotHeightPx : 0}
              />
            </AnimatedComponentWrapper>
          </div>
        );
      })}
    </div>
    </CommerceSectionExpandProvider>
  );
}

export default function SiteRenderer({
  sections,
  pageMenu,
  pageBackground,
  typography,
  viewport: forcedViewport,
  interactive = true,
  liveHubupFetch = false,
  checkoutSlug,
  commerceCapabilities,
  onCommerceNavigate,
  site,
  onOrderComplete,
  showBrandingFooter = false,
  playAnimations = true,
  ambientMusicPlayback,
}) {
  const [viewport, setViewport] = useState(forcedViewport || 'desktop');
  const [purchaseProduct, setPurchaseProduct] = useState(null);
  const jardinsSite = siteUsesDesignKit(site, 'jardins');
  const layoutWidth = useJardinsLayoutWidth(jardinsSite);

  useEffect(() => {
    if (forcedViewport) return undefined;
    const update = () => setViewport(detectPublicViewport(window.innerWidth));
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [forcedViewport]);

  const pageStyle = {
    ...resolveBackgroundStyle(pageBackground, '#ffffff'),
    ...resolveSiteFontStyle(typography),
  };

  const activeViewport = forcedViewport || viewport;
  const jardinsFluidScale =
    jardinsSite && activeViewport === 'desktop'
      ? resolveJardinsFluidScale(layoutWidth, activeViewport)
      : 1;

  const pageMenuVisible =
    pageMenu && !isComponentHidden(pageMenu, activeViewport);
  const pageMenuOverlayHero =
    pageMenuVisible && pageMenu.props?.overlayFirstSection === true;
  const { ref: pageMenuOverlayRef, marginBottom: pageMenuOverlayPull } =
    useOverlayMenuPull(pageMenuOverlayHero);
  const ambientPlayback =
    ambientMusicPlayback !== undefined ? ambientMusicPlayback : interactive;
  const jardinsCompactHeaderVisible = (sections || []).some(
    (s) =>
      shouldUseJardinsCompactHeader(s, activeViewport) &&
      !isSectionHidden(s, activeViewport),
  );
  const showFloatingAmbientMute =
    hasAmbientMusic(site?.ambientMusic) &&
    !pageMenuVisible &&
    !jardinsCompactHeaderVisible;

  return (
    <SiteAmbientMusicProvider
      config={site?.ambientMusic}
      siteSlug={site?.slug || checkoutSlug}
      playbackEnabled={ambientPlayback}
    >
      <SiteFontStyles typography={typography} />
      <ProductPurchaseModal
        open={Boolean(purchaseProduct)}
        product={purchaseProduct}
        checkoutSlug={checkoutSlug}
        onClose={() => setPurchaseProduct(null)}
      />
      <div
        className={`w-full wc-site-viewport wc-site-viewport--${activeViewport} ${
          siteUsesDesignKit(site, 'jardins') ? 'wc-site--jardins' : ''
        } ${showBrandingFooter ? 'min-h-0' : 'min-h-screen'}`}
        data-wc-viewport={activeViewport}
        style={{
          ...pageStyle,
          ...(jardinsSite && activeViewport === 'desktop'
            ? {
                '--wc-jardins-fluid': jardinsFluidScale,
              }
            : {}),
        }}
      >
        {pageMenuVisible ? (
          <div
            ref={pageMenuOverlayRef}
            className={`w-full sticky top-0 z-50 ${
              pageMenuOverlayHero ? 'bg-transparent' : ''
            }`}
            style={
              pageMenuOverlayPull != null ? { marginBottom: pageMenuOverlayPull } : undefined
            }
          >
            <AnimatedComponentWrapper
              comp={pageMenu}
              viewport={activeViewport}
              disabled={!playAnimations}
            >
              <RenderComponent
                comp={pageMenu}
                sections={sections}
                interactive={interactive}
                editorViewport={activeViewport}
                siteTypography={typography}
                checkoutSlug={checkoutSlug}
                onProductBuy={setPurchaseProduct}
                commerceCapabilities={commerceCapabilities}
                onCommerceNavigate={onCommerceNavigate}
                site={site}
                onOrderComplete={onOrderComplete}
              />
            </AnimatedComponentWrapper>
          </div>
        ) : null}
        {(sections || []).map((section) => {
          const activeViewport = forcedViewport || viewport;
          if (isSectionHidden(section, activeViewport)) {
            return null;
          }
          const kit = getSectionDesignKit(section);
          const tone = section.kitSectionTone
            ? `wc-kit-section--${section.kitSectionTone}`
            : '';
          const heroFill = isJardinsHeroViewportFill(section, activeViewport);
          return (
          <section
            key={section.id}
            id={section.anchor}
            className={`relative w-full ${kit?.sectionClass || ''} ${tone}`.trim()}
            style={{
              paddingTop:
                resolveJardinsSectionPadding(section, activeViewport) ??
                (kit ? 0 : 64),
              paddingBottom:
                resolveJardinsSectionPadding(section, activeViewport) ??
                (kit ? 0 : 64),
              ...(heroFill ? { minHeight: '100dvh' } : {}),
            }}
          >
            <SectionBackground section={section} />
            {section.kitSectionTone === 'hero-dark' &&
            !resolveSectionBackgroundOverlayStyle(section) ? (
              <div className="wc-kit-hero-shade absolute inset-0 z-[1]" aria-hidden />
            ) : null}
            <div
              className={`relative z-[2]${heroFill ? ' min-h-[inherit] h-full flex flex-col' : ''}`}
            >
              <SectionAnimationProvider section={section} disabled={!playAnimations}>
                <SectionGrid
                  section={section}
                  editorMode={undefined}
                  sections={sections}
                  viewport={forcedViewport || viewport}
                  jardinsFluidScale={jardinsFluidScale}
                  interactive={interactive}
                  siteTypography={typography}
                  liveHubupFetch={liveHubupFetch}
                  checkoutSlug={checkoutSlug}
                  onProductBuy={setPurchaseProduct}
                  commerceCapabilities={commerceCapabilities}
                  onCommerceNavigate={onCommerceNavigate}
                  site={site}
                  onOrderComplete={onOrderComplete}
                  playAnimations={playAnimations}
                />
              </SectionAnimationProvider>
            </div>
          </section>
          );
        })}
        {siteUsesDesignKit(site, 'jardins') ? (
          <JardinsMobileBookingBar
            sections={sections}
            interactive={interactive}
            viewport={activeViewport}
          />
        ) : null}
        {showBrandingFooter ? <JustOnePageSiteFooter /> : null}
        <AmbientMusicFixedControl enabled={showFloatingAmbientMute} />
      </div>
    </SiteAmbientMusicProvider>
  );
}

export { SectionGrid };
